import { cloneElement, useId, useState, type JSX, type ReactElement, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface HintTooltipProps {
  /** The hint. Empty keeps the tooltip closed, for hints that only apply in some states. */
  content: ReactNode;
  /** One element that forwards its ref and props: a button, link, checkbox or select trigger. */
  children: ReactElement<{ disabled?: boolean; 'aria-describedby'?: string }>;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * shadcn Tooltip in place of a native `title` hint on an interactive control, so the hint
 * opens on keyboard focus too. A native `title` is still fine for the full text of truncated static text.
 */
export function HintTooltip({ content, children, side }: Readonly<HintTooltipProps>): JSX.Element {
  const [open, setOpen] = useState(false);
  const descriptionId = useId();
  const hasContent = Boolean(content);
  return (
    <Tooltip open={hasContent && open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        {hasContent && children.props.disabled ? (
          // A disabled control can't take focus and may drop pointer events: the wrapper
          // takes the hover, and the control keeps the hint as its accessible description.
          <span className="inline-flex">
            {cloneElement(children, {
              'aria-describedby': [children.props['aria-describedby'], descriptionId].filter(Boolean).join(' '),
            })}
            <span id={descriptionId} hidden>
              {content}
            </span>
          </span>
        ) : (
          children
        )}
      </TooltipTrigger>
      {hasContent && (
        <TooltipContent side={side} className="max-w-xs break-words">
          {content}
        </TooltipContent>
      )}
    </Tooltip>
  );
}
